import type { ReactNode } from "react";
import type {
  ChangeRequestOperation,
  ChangeRequestView,
} from "@KassaroAB/shared/types/changeRequest";
import {
  CHANGE_REQUEST_DOMAIN_TARGET_KEY,
  CHANGE_REQUEST_OPERATION_DOMAIN,
  CHANGE_REQUEST_REFERENCE_FIELDS,
  CHANGE_REQUEST_SINGLETON_CONTEXT_ID,
  type ChangeRequestDomain,
  getChangeRequestOperationKind,
} from "@KassaroAB/shared/types/changeRequestDomains";
import type { ChangeRequestOperationType } from "@KassaroAB/shared/types/enums/ChangeRequestOperationType";
import {
  isChangeRequestOperationRef,
  parseChangeRequestOperationRef,
} from "@KassaroAB/shared/types/changeRequestRefs";
import type { Money } from "@KassaroAB/shared/types/money";
import { formatMoney } from "./formatMoney";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isMoney = (value: unknown): value is Money =>
  typeof value === "object" &&
  value !== null &&
  "amount" in value &&
  "currency" in value &&
  typeof value.amount === "number" &&
  typeof value.currency === "string";

export const humanizeFieldKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (character) => character.toUpperCase())
    .trim();

export const getRecordFieldValue = (
  record: Record<string, unknown> | undefined,
  key: string,
): unknown => {
  if (record === undefined) {
    return undefined;
  }
  return record[key];
};

const getPayloadRecord = (
  operation: ChangeRequestOperation,
): Record<string, unknown> | null => {
  const payload: unknown = operation.payload;
  return isRecord(payload) ? payload : null;
};

export const getOperationDomain = (
  operation: ChangeRequestOperation,
): ChangeRequestDomain => CHANGE_REQUEST_OPERATION_DOMAIN[operation.type];

export const getEntityDisplayName = (
  changeRequestView: ChangeRequestView,
  domain: ChangeRequestDomain,
  id: string,
): string => changeRequestView.context[domain]?.[id]?.displayName ?? id;

const getCreateOperationName = (
  operation: ChangeRequestOperation,
): string | null => {
  if (getChangeRequestOperationKind(operation.type) !== "create") {
    return null;
  }
  const name = getPayloadRecord(operation)?.name;
  return typeof name === "string" ? name : null;
};

export const getOperationRefDisplayName = (
  changeRequestView: ChangeRequestView,
  operationRef: string,
): string => {
  if (!isChangeRequestOperationRef(operationRef)) {
    return operationRef;
  }
  const { operationId } = parseChangeRequestOperationRef(operationRef);
  const pendingOperation = changeRequestView.operations.find(
    (operation) => operation.operationId === operationId,
  );
  if (pendingOperation === undefined) {
    return operationRef;
  }
  return getCreateOperationName(pendingOperation) ?? operationRef;
};

const formatReference = (
  changeRequestView: ChangeRequestView,
  domain: ChangeRequestDomain,
  value: unknown,
): string => {
  if (typeof value !== "string") {
    return String(value);
  }
  if (isChangeRequestOperationRef(value)) {
    return getOperationRefDisplayName(changeRequestView, value);
  }
  return getEntityDisplayName(changeRequestView, domain, value);
};

const VAT_FIELDS = new Set(["VAT", "takeawayVAT", "defaultVAT"]);

export const LAYOUT_ITEM_FIELDS = new Set([
  "items",
  "topButtons",
  "bottomButtons",
  "leftButtons",
  "rightButtons",
  "checkoutButtons",
]);

export const getLayoutItemTypeLabelKey = (item: unknown): string =>
  `storeChatSessions.changeRequest.layoutItemTypes.${isRecord(item) ? String(item.type) : ""}`;

export const formatLayoutItem = (
  changeRequestView: ChangeRequestView,
  item: unknown,
  translate?: (key: string) => string,
): string => {
  if (!isRecord(item)) {
    return String(item);
  }
  if (typeof item.text === "string" && item.text.length > 0) {
    return item.text;
  }
  const reference = Object.entries(item).find(
    ([key, value]) =>
      CHANGE_REQUEST_REFERENCE_FIELDS[key] !== undefined &&
      typeof value === "string",
  );
  if (reference !== undefined) {
    const [key, value] = reference;
    const domain = CHANGE_REQUEST_REFERENCE_FIELDS[key];
    if (domain !== undefined) {
      return formatReference(changeRequestView, domain, value);
    }
  }
  const fallback = humanizeFieldKey(
    String(item.type ?? "")
      .toLowerCase()
      .replace(/_/g, " "),
  );
  if (translate === undefined) {
    return fallback;
  }
  const labelKey = getLayoutItemTypeLabelKey(item);
  const label = translate(labelKey);
  return label === labelKey ? fallback : label;
};

const formatNamedEntries = (entries: unknown[]): string =>
  entries
    .map((entry) =>
      isRecord(entry) && typeof entry.name === "string"
        ? entry.name
        : String(entry),
    )
    .join(", ");

export const formatChangeRequestValue = (
  fieldKey: string,
  value: unknown,
  changeRequestView: ChangeRequestView,
): ReactNode => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string" && isChangeRequestOperationRef(value)) {
    return getOperationRefDisplayName(changeRequestView, value);
  }
  if (LAYOUT_ITEM_FIELDS.has(fieldKey) && Array.isArray(value)) {
    return value.length === 0
      ? "—"
      : value
          .map((item) => formatLayoutItem(changeRequestView, item))
          .join(", ");
  }
  const referenceDomain = CHANGE_REQUEST_REFERENCE_FIELDS[fieldKey];
  if (referenceDomain !== undefined) {
    const values = Array.isArray(value) ? value : [value];
    return values.length === 0
      ? "—"
      : values
          .map((entry) =>
            formatReference(changeRequestView, referenceDomain, entry),
          )
          .join(", ");
  }
  if (fieldKey === "tables" && Array.isArray(value)) {
    return formatNamedEntries(value);
  }
  if (VAT_FIELDS.has(fieldKey) && typeof value === "number") {
    return `${value} %`;
  }
  if (isMoney(value)) {
    return formatMoney(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "—" : JSON.stringify(value);
  }
  return JSON.stringify(value);
};

const getOperationTargetId = (
  operation: ChangeRequestOperation,
): string | null => {
  const targetKey =
    CHANGE_REQUEST_DOMAIN_TARGET_KEY[getOperationDomain(operation)];
  if (targetKey === null) {
    return CHANGE_REQUEST_SINGLETON_CONTEXT_ID;
  }
  const targetId = getPayloadRecord(operation)?.[targetKey];
  return typeof targetId === "string" ? targetId : null;
};

export const getOperationPreviewName = (
  changeRequestView: ChangeRequestView,
  operation: ChangeRequestOperation,
): string => {
  const createName = getCreateOperationName(operation);
  if (createName !== null) {
    return createName;
  }
  const targetId = getOperationTargetId(operation);
  if (targetId === null) {
    return operation.operationId;
  }
  if (targetId === CHANGE_REQUEST_SINGLETON_CONTEXT_ID) {
    return (
      changeRequestView.context[getOperationDomain(operation)]?.[targetId]
        ?.displayName ?? ""
    );
  }
  return formatReference(
    changeRequestView,
    getOperationDomain(operation),
    targetId,
  );
};

export const getOperationTypeLabelKey = (
  operationType: ChangeRequestOperationType,
): string => `storeChatSessions.changeRequest.operations.${operationType}`;

export const getDomainLabelKey = (domain: ChangeRequestDomain): string =>
  `storeChatSessions.changeRequest.domains.${domain}`;

export const getOperationChanges = (
  operation: ChangeRequestOperation,
): Record<string, unknown> | null => {
  if (getChangeRequestOperationKind(operation.type) !== "update") {
    return null;
  }
  const changes = getPayloadRecord(operation)?.changes;
  return isRecord(changes) ? changes : null;
};

export const getOperationBeforeRecord = (
  changeRequestView: ChangeRequestView,
  operation: ChangeRequestOperation,
): Record<string, unknown> | undefined => {
  const targetId = getOperationTargetId(operation);
  if (targetId === null) {
    return undefined;
  }
  return changeRequestView.context[getOperationDomain(operation)]?.[targetId]
    ?.record;
};

export const getOperationCreateRecord = (
  operation: ChangeRequestOperation,
): Record<string, unknown> | null =>
  getChangeRequestOperationKind(operation.type) === "create"
    ? getPayloadRecord(operation)
    : null;

export const summarizeChanges = (
  changes: Record<string, unknown>,
  fieldLabel: (key: string) => string = humanizeFieldKey,
): string =>
  Object.keys(changes)
    .filter((key) => changes[key] !== undefined)
    .map(fieldLabel)
    .join(", ");

export const groupOperationsByDomain = (
  operations: ChangeRequestOperation[],
): { domain: ChangeRequestDomain; operations: ChangeRequestOperation[] }[] => {
  const groups = new Map<ChangeRequestDomain, ChangeRequestOperation[]>();
  for (const operation of operations) {
    const domain = getOperationDomain(operation);
    groups.set(domain, [...(groups.get(domain) ?? []), operation]);
  }
  return [...groups.entries()].map(([domain, domainOperations]) => ({
    domain,
    operations: domainOperations,
  }));
};
